import React, { useContext, useState } from 'react'
import { Popover, IconButton, Tooltip, useTheme } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { ApiDataWrapperContext } from '../contexts/ApiDataWrapper'
import LinkWithQuery from '../components/LinkWithQuery'
import Text from '../components/Text'
import formatPhoneBR from '../libs/formatPhoneBR'

// O chip de conexão substitui o bloco de credenciais que ocupava 300px no topo
// da barra lateral antiga. Fica sempre visível na faixa do topo e abre um
// painel com API, secret e servidor — quem integra continua achando a chave em
// dois cliques, e quem não integra nunca mais esbarra nela.

// Campo com botão de copiar, herdado da barra lateral antiga.
function CampoCopiavel({ label, valor, mascarado = false }) {
  const theme = useTheme()
  const [copiado, setCopiado] = useState(false)
  const [revelado, setRevelado] = useState(false)

  const copiar = async () => {
    if (!valor) return
    try {
      await navigator.clipboard.writeText(valor)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 1500)
    } catch (e) {
      // navegador sem permissão de área de transferência — nada a fazer
    }
  }

  const escondido = mascarado && !revelado

  return (
    <div
      className="flex items-center gap-2 py-2"
      style={{ borderBottom: `1px solid ${theme.palette.divider}` }}
    >
      <div className="flex-1 min-w-0">
        <Text className="text-[10px] font-bold uppercase tracking-wider opacity-50">
          {label}
        </Text>
        <div className="h-[18px] flex items-center">
          {escondido
            ? <span className="block w-[110px] h-[10px] rounded bg-gray-500 bg-opacity-40" />
            : (
              <Text
                className="text-xs truncate"
                style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
              >
                {valor || '—'}
              </Text>
            )}
        </div>
      </div>

      {mascarado && (
        <Tooltip title={revelado ? 'Esconder' : 'Mostrar'} arrow>
          <IconButton size="small" onClick={() => setRevelado(!revelado)} sx={{ opacity: 0.6 }}>
            {revelado
              ? <VisibilityOffIcon style={{ fontSize: 15 }} />
              : <VisibilityIcon style={{ fontSize: 15 }} />}
          </IconButton>
        </Tooltip>
      )}

      {valor && (
        <Tooltip title={copiado ? 'Copiado!' : 'Copiar'} arrow>
          <IconButton size="small" onClick={copiar} sx={{ opacity: 0.6 }}>
            {copiado
              ? <CheckIcon style={{ fontSize: 15, color: theme.palette.primary.main }} />
              : <ContentCopyIcon style={{ fontSize: 15 }} />}
          </IconButton>
        </Tooltip>
      )}
    </div>
  )
}

export default function ConnectionChip() {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState(null)
  const {
    server, phone, provider, statusSession, verifiedName, getCredentials
  } = useContext(ApiDataWrapperContext)
  const { api, secret } = getCredentials()

  const conectado = statusSession === 'isLogged'
  const desconectado = statusSession === 'notLogged'
  const carregando = !conectado && !desconectado
  const ehMetaOficial = provider === 'hiperapi-v3-cloud-official'
  const telefone = formatPhoneBR(phone)

  // Enquanto o status não chega, mostra esqueleto em vez de mentir "desconectado".
  if (carregando) {
    return (
      <div
        className="flex-none flex items-center gap-2 rounded-full px-3 py-[6px]"
        style={{ border: `1px solid ${theme.palette.divider}` }}
        aria-label="Verificando a conexão"
      >
        <span className="w-[9px] h-[9px] rounded-full bg-gray-500 bg-opacity-40" />
        <span className="w-[70px] h-[10px] rounded bg-gray-500 bg-opacity-20" />
      </div>
    )
  }

  return (
    <>
      <button
        onClick={e => setAnchorEl(e.currentTarget)}
        aria-expanded={Boolean(anchorEl)}
        aria-haspopup="dialog"
        className="foco-navegacao flex-none flex items-center gap-2 rounded-full px-3 py-[6px] transition-colors"
        style={{
          border: `1px solid ${anchorEl ? theme.palette.primary.main : theme.palette.divider}`,
          color: theme.palette.text.primary
        }}
      >
        {conectado
          ? <CheckCircleIcon style={{ fontSize: 17, color: theme.palette.success.main }} />
          : <DoNotDisturbOnIcon style={{ fontSize: 17, opacity: 0.6 }} />}

        <span className="text-xs font-medium">
          {conectado ? 'Conectado' : 'Desconectado'}
        </span>

        {/* O telefone só aparece a partir de 1280px: abaixo disso a faixa já
            está disputando espaço com as abas. */}
        {conectado && telefone && (
          <span className="text-xs opacity-60 hidden xl:inline">{telefone}</span>
        )}

        <ExpandMoreIcon style={{ fontSize: 16, opacity: 0.5 }} />
      </button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            // maxWidth impede o painel de encostar nas bordas em tela de 360px.
            sx: { mt: 1, width: 320, maxWidth: 'calc(100vw - 24px)', p: 2, borderRadius: 2 }
          }
        }}
      >
        <div className="flex items-center gap-2 pb-2">
          {conectado
            ? <CheckCircleIcon style={{ fontSize: 19, color: theme.palette.success.main }} />
            : <DoNotDisturbOnIcon style={{ fontSize: 19, opacity: 0.6 }} />}
          <Text className="font-black text-sm">
            {conectado ? 'Número conectado' : 'Número desconectado'}
          </Text>
        </div>

        {desconectado && (
          <LinkWithQuery to="/" style={{ textDecoration: 'none' }} onClick={() => setAnchorEl(null)}>
            <div
              className="rounded-lg px-3 py-2 mb-2 text-center text-xs font-black"
              style={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.getContrastText(theme.palette.primary.main)
              }}
            >
              RECONECTAR MEU NÚMERO
            </div>
          </LinkWithQuery>
        )}

        {verifiedName && <CampoCopiavel label="Conta" valor={verifiedName} />}
        <CampoCopiavel label="Telefone" valor={telefone} />
        <CampoCopiavel label="Minha API" valor={api} />
        <CampoCopiavel label="Secret" valor={secret} mascarado />
        <CampoCopiavel
          label="Versão"
          valor={ehMetaOficial ? 'Meta Cloud API Oficial' : provider}
        />
        <CampoCopiavel label="Servidor" valor={server ? `Servidor ${server}` : ''} />
      </Popover>
    </>
  )
}
